import { MutableRefObject, useEffect, useRef } from 'react';
import { Feature } from 'geojson';
import { noop } from 'lodash';
import mapboxgl from 'mapbox-gl';
// @ts-expect-error mapbox-gl-draw has no types
import MapboxDraw from '@mapbox/mapbox-gl-draw';

const useInitializeDrawModeEffect = ({
  features,
  setFeatures,
  mapLoaded,
  mapRef,
  onEnter = noop,
  onExit = noop,
}: {
  features: Feature[];
  setFeatures: (features: Feature[]) => void;
  mapLoaded: boolean;
  mapRef: MutableRefObject<mapboxgl.Map | null>;
  onEnter?: () => void;
  onExit?: () => void;
}) => {
  const drawRef = useRef<MapboxDraw | null>(null);

  // Initialize draw control when map is loaded
  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      if (!drawRef.current) {
        drawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: true,
            trash: true,
          },
          defaultMode: 'simple_select',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]);

  // Setup draw listeners when draw control exists
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return noop; // Ensure return type is a function

    const handleCreate = () => {
      const all = drawRef.current?.getAll();
      setFeatures(all?.features || []);
    };
    const handleUpdate = () => {
      const all = drawRef.current?.getAll();
      setFeatures(all?.features || []);
    };
    const handleDelete = () => {
      const all = drawRef.current?.getAll();
      setFeatures(all?.features || []);
    };

    map.on('draw.create', handleCreate);
    map.on('draw.update', handleUpdate);
    map.on('draw.delete', handleDelete);

    return () => {
      map.off('draw.create', handleCreate);
      map.off('draw.update', handleUpdate);
      map.off('draw.delete', handleDelete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]);

  const enterDrawMode = () => {
    if (!mapRef.current || !drawRef.current) return;

    // Add draw controls to the map
    if (!mapRef.current.hasControl(drawRef.current)) {
      mapRef.current.addControl(drawRef.current, 'bottom-right');
    }

    // Add all existing drawn features to the drawRef for editing
    drawRef.current.deleteAll(); // Clear any existing features in the drawRef
    features.forEach((feature) => {
      drawRef.current.add(feature);
    });

    drawRef.current.changeMode(
      features.length ? 'simple_select' : 'draw_polygon',
    );
    onEnter();
  };

  const exitDrawMode = () => {
    if (!mapRef.current || !drawRef.current) return;

    // Remove draw controls from the map
    if (mapRef.current.hasControl(drawRef.current)) {
      mapRef.current.removeControl(drawRef.current);
    }
    onExit();
  };

  // Exit draw mode if the map refreshes
  useEffect(() => {
    if (!mapLoaded) {
      exitDrawMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]);

  return {
    enterDrawMode,
    exitDrawMode,
    drawRef,
  };
};

export default useInitializeDrawModeEffect;
